import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { servicesAPI, getImageUrl } from "../../services/api";
import { useBookingModal } from "../../context/BookingModalContext";
import Reveal from "../Shared/Reveal";
import s1 from "../../assets/img/s1.jpg";
import "./ServiceDetail.scss";
import { normalizeRichText } from "../../Utils/stripHtml";

const buildDefaultContent = (title) => {
  const name = (title || "this service").toLowerCase();
  return [
    `Our experienced physiotherapists provide expert ${name} tailored to your individual needs. We combine hands-on treatment, targeted exercise and patient education to help you achieve the best possible outcome.`,
    `During your first visit, our team will take a detailed history and conduct a thorough assessment to understand your goals. From there, we'll build a personalised treatment plan based on your specific needs and lifestyle.`,
    `Treatment may include manual therapy, individualised exercise programs, movement education, and ongoing progress reviews to keep you on track towards your goals.`,
    `Ready to get started with ${name}? Book an appointment with our team today.`,
  ]
    .map((para) => `<p>${para}</p>`)
    .join("");
};

const ServiceDetail = () => {
  const { id } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { openModal } = useBookingModal();

  useEffect(() => {
    const fetchService = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await servicesAPI.getById(id);
        setService(response.data);
      } catch (err) {
        setError(err.response?.status === 404 ? "Service not found" : "Failed to load service");
      } finally {
        setLoading(false);
      }
    };
    fetchService();
  }, [id]);

  if (loading) {
    return (
      <div className="service-detail-loading">
        <div className="loading-message">Loading...</div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="service-detail-error">
        <div className="error-message">
          <h2>Service Not Found</h2>
          <p>{error || "The service you are looking for does not exist."}</p>
          <Link to="/services" className="site__button site__button_style--solid site__button_shape--default">
            <span className="text">Back to Services</span>
          </Link>
        </div>
      </div>
    );
  }

  const contentHtml = normalizeRichText(service.content) || buildDefaultContent(service.title);

  return (
    <div className="main main_service_detail">
      <div className="page-hero">
        <div className="page-hero__content">
          <span className="small__text">WHAT WE OFFER</span>
          <h1 className="headline__text">{service.title}</h1>
          {service.description && (
            <p className="page-hero__subtitle">{service.description}</p>
          )}
        </div>
      </div>

      <div className="service-detail-section">
        <div className="service-detail-container">
          <Link to="/services" className="back-to-services">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            Back to Services
          </Link>

          <Reveal as="div" direction="fade" className="service-detail-image">
            <img
              src={service.image ? getImageUrl(service.image) : s1}
              alt={service.title}
              onError={(e) => { e.target.src = s1; }}
            />
          </Reveal>

          <Reveal as="div" direction="up" className="service-detail-content">
            <div dangerouslySetInnerHTML={{ __html: contentHtml }} />
          </Reveal>

          <Reveal as="div" direction="up" className="service-detail-cta">
            <h2>Ready to book this service?</h2>
            <p>Book an appointment and our physiotherapists will build a personalised treatment plan just for you.</p>
            <button
              type="button"
              onClick={() => setTimeout(openModal, 0)}
              className="site__button site__button_style--solid site__button_shape--default"
            >
              <span className="text">Book an Appointment</span>
            </button>
          </Reveal>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetail;
